(() => {
  const excludedHandles = new Set(['board-assembly-free', 'no-skate-tool', 'ill-set-it-up-myself-free']);
  const cache = new Map();
  const tokens = (value) => String(value || '')
    .normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase()
    .replace(/['’]/g, '')
    .match(/[\p{L}\p{N}]+(?:\.\d+)?/gu) || [];

  // Require every query word to appear in a name, brand or product type.
  // Word prefixes support typing; numbers match exactly so 8.0 doesn't mean 8.025.
  const matches = (product, query) => {
    if (!product || excludedHandles.has(product.handle)) return false;
    const terms = tokens(query);
    if (!terms.length) return false;
    const words = tokens([product.title, product.vendor, product.type].filter(Boolean).join(' '));
    return terms.every((term) => words.some((word) => /^\d+(?:\.\d+)?$/.test(term)
      ? /^\d+(?:\.\d+)?$/.test(word) && Number(word) === Number(term)
      : word.startsWith(term) || (term.length > 3 && term.endsWith('s') && word === term.slice(0, -1))));
  };

  const search = async (query, { searchUrl = '/search', signal } = {}) => {
    const checkCancelled = () => {
      if (signal?.aborted) throw new DOMException('Search cancelled', 'AbortError');
    };
    checkCancelled();
    const endpoint = new URL(searchUrl, window.location.origin);
    endpoint.searchParams.set('type', 'product');
    endpoint.searchParams.set('q', query);
    endpoint.searchParams.set('options[prefix]', 'last');
    endpoint.searchParams.set('options[unavailable_products]', 'last');
    endpoint.searchParams.set('section_id', 'hp-search-products');
    endpoint.searchParams.delete('page');
    const cacheKey = endpoint.toString();
    const cached = cache.get(cacheKey);
    if (cached && cached.expires > Date.now()) return cached.products;

    const products = new Map();
    const visited = new Set();
    let next = endpoint;
    while (next) {
      checkCancelled();
      next.searchParams.set('section_id', 'hp-search-products');
      if (next.origin !== endpoint.origin || visited.has(next.toString())) {
        throw new Error('Search returned an invalid next page.');
      }
      visited.add(next.toString());
      const response = await fetch(next.toString(), { signal });
      if (!response.ok) throw new Error(`Search request failed with ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = JSON.parse(doc.querySelector('[data-hp-search-products]')?.textContent || 'null');
      if (!Array.isArray(payload?.products) || !Object.hasOwn(payload, 'next')) {
        throw new Error('Search returned incomplete product data.');
      }
      payload.products.forEach((product) => {
        if (product.id && matches(product, query)) products.set(String(product.id), product);
      });
      next = payload.next ? new URL(payload.next, endpoint) : null;
    }
    checkCancelled();
    const result = Array.from(products.values());
    if (cache.size >= 20) cache.delete(cache.keys().next().value);
    cache.set(cacheKey, { products: result, expires: Date.now() + 60000 });
    return result;
  };

  window.HpProductSearch = Object.freeze({ matches, search });
})();
